import React from "react"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

import { getImageUrl, getPostUrl, getCategoryUrl } from "../utilities"

import useSiteMetadata from "../hooks/use-site-metadata"

const CommonSEO = props => {
  const { title: siteTitle, description: siteDescription, lang, facebookAppId } = useSiteMetadata()

  const {
    title = siteTitle,
    description = siteDescription,
    type,
    pageUrl,
    nextPageUrl,
    prevPageUrl,
    imageUrl,
    imageWidth = 1200,
    imageHeight = 630,
    schemaOrgJSONLD,
  } = props

  const meta = [
    {
      name: `description`,
      content: description,
    },
    facebookAppId && {
      property: `fb:app_id`,
      content: facebookAppId,
    },
    {
      property: `og:url`,
      content: pageUrl,
    },
    {
      property: `og:title`,
      content: title,
    },
    {
      property: `og:description`,
      content: description,
    },
    {
      property: `og:type`,
      content: type,
    },
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:title`,
      content: title,
    },
    {
      name: `twitter:description`,
      content: description,
    },
  ]

  if (imageUrl) {
    meta.push(
      {
        name: `twitter:image`,
        content: imageUrl,
      },
      {
        property: `og:image`,
        content: imageUrl,
      },
      {
        property: `og:image:width`,
        content: imageWidth,
      },
      {
        property: `og:image:height`,
        content: imageHeight,
      }
    )
  }

  return (
    <Helmet htmlAttributes={{ lang }} title={title} meta={meta}>
      <script type="application/ld+json">{JSON.stringify(schemaOrgJSONLD)}</script>

      <link rel="canonical" href={pageUrl} />

      {prevPageUrl ? <link rel="prev" href={prevPageUrl} /> : null}
      {nextPageUrl ? <link rel="next" href={nextPageUrl} /> : null}
    </Helmet>
  )
}

const HomeSEO = () => {
  const data = useStaticQuery(graphql`
    query {
      file(name: { eq: "share" }) {
        publicURL
      }
    }
  `)
  const { canonicalUrl, title } = useSiteMetadata()

  return (
    <CommonSEO
      pageUrl={canonicalUrl}
      type="website"
      imageUrl={data.file.publicURL}
      schemaOrgJSONLD={[
        {
          "@context": `http://schema.org`,
          "@type": `WebSite`,
          url: canonicalUrl,
          name: title,
        },
      ]}
    />
  )
}

const CategorySEO = ({ title, category, posts = [], page = 1, pageCount }) => {
  const {
    baseSite: { url: baseUrl },
    canonicalUrl,
    shortTitle,
  } = useSiteMetadata()

  const imageUrl = category.launchImage
    ? `${getImageUrl(category.launchImage.guid, `image`, category.launchImage.platformSlug)}`
    : null

  let breadcrumbList = []
  let parentCategory = category.parentCategory

  if (parentCategory) {
    while (parentCategory !== null) {
      breadcrumbList.unshift({
        url: `${canonicalUrl}${getCategoryUrl(parentCategory.slug)}`,
        name: parentCategory.name,
      })

      parentCategory = parentCategory.parentCategory
    }
  }

  const pageUrl = `${canonicalUrl}${getCategoryUrl(category.slug, page)}`
  const nextPageUrl = page < pageCount ? `${canonicalUrl}${getCategoryUrl(category.slug, page + 1)}` : null
  const prevPageUrl = page > 1 ? `${canonicalUrl}${getCategoryUrl(category.slug, page - 1)}` : null

  return (
    <CommonSEO
      title={title}
      description={category.abstract}
      pageUrl={pageUrl}
      nextPageUrl={nextPageUrl}
      prevPageUrl={prevPageUrl}
      imageUrl={imageUrl}
      schemaOrgJSONLD={[
        getBreadcrumbSchema(breadcrumbList, baseUrl, canonicalUrl, shortTitle),
        {
          "@context": `http://schema.org`,
          "@type": `ItemList`,
          itemListElement: posts.map((post, index) => ({
            "@type": `ListItem`,
            position: index + 1,
            url: `${canonicalUrl}${getPostUrl(post.slug)}`,
          })),
        },
      ]}
    />
  )
}

const BlogPostSEO = ({
  post: {
    featuredImage,
    slug,
    metaDescription,
    abstract,
    author,
    publishedAt,
    modifiedAt,
    title,
    alias,
    mainCategories,
  },
}) => {
  const { siteUrl, canonicalUrl, shortTitle, publisher } = useSiteMetadata()
  const imageUrl = `${getImageUrl(featuredImage.guid, featuredImage.type, featuredImage.platformSlug)}`
  const pageUrl = `${canonicalUrl}${getPostUrl(slug)}`
  const description = metaDescription || abstract

  let breadcrumbList = []
  if (mainCategories) {
    breadcrumbList = mainCategories.map(c => ({
      url: `${canonicalUrl}${getCategoryUrl(c.slug)}`,
      name: c.name,
    }))
  }

  const blogPostSchema = {
    "@context": `http://schema.org`,
    "@type": `BlogPosting`,
    url: pageUrl,
    mainEntityOfPage: {
      "@type": `WebPage`,
      "@id": pageUrl,
    },
    headline: title,
    author: author
      ? {
          "@type": `Person`,
          name: author,
        }
      : {
          "@type": `Organization`,
          name: publisher.name,
        },
    datePublished: publishedAt,
    dateModified: modifiedAt,
    publisher: {
      "@type": `Organization`,
      name: publisher.name,
      url: siteUrl || canonicalUrl,
      logo: {
        "@type": `ImageObject`,
        url: `${canonicalUrl}/img/publisher.png`,
        height: 60,
        width: publisher.logoWidth,
      },
    },
    image: {
      "@type": `ImageObject`,
      url: imageUrl,
    },
    description,
  }

  return (
    <CommonSEO
      title={alias || title}
      description={description}
      type="article"
      pageUrl={pageUrl}
      imageUrl={imageUrl}
      schemaOrgJSONLD={[getBreadcrumbSchema(breadcrumbList, siteUrl, canonicalUrl, shortTitle), blogPostSchema]}
    />
  )
}

const getBreadcrumbSchema = (paths, siteUrl, homeUrl, shortTitle) => {
  const items = []

  if (siteUrl !== homeUrl) {
    items.push({
      "@type": `ListItem`,
      position: 1,
      item: {
        "@id": `${siteUrl}/`,
        name: `Home`,
      },
    })
  }

  return {
    "@context": `http://schema.org`,
    "@type": `BreadcrumbList`,
    itemListElement: [
      ...items,
      {
        "@type": `ListItem`,
        position: items.length + 1,
        item: {
          "@id": `${homeUrl}/`,
          name: items.length ? shortTitle : `Home`,
        },
      },
      ...paths.map((p, i) => ({
        "@type": `ListItem`,
        position: i + items.length + 2,
        item: {
          "@id": p.url,
          name: p.name,
        },
      })),
    ],
  }
}

export default CommonSEO
export { HomeSEO, CategorySEO, BlogPostSEO }
